// 調整舞台面板 / 提示列上的字（集中在這裡，才測得到：白話、沒有英文叫人的字、沒有 emoji）

import type { Mechanism } from '../mechanisms/model';
import { formatMechValue } from '../mechanisms/motion';
import type { ScopeKey } from './scope';

/** 機關是哪一種（面板上機關名字旁邊的小字） */
export function kindLabel(def: Pick<Mechanism, 'kind' | 'variant' | 'axis'>): string {
  if (def.variant === 'hoist') return '吊點';
  if (def.variant === 'arc') return '沿弧形軌道';
  if (def.kind === 'rotary') return Math.abs(def.axis.y) > 0.7 ? '旋轉' : '翻轉';
  return Math.abs(def.axis.y) > 0.7 ? '升降' : '直線';
}

export function valueText(v: number, kind: Mechanism['kind']): string {
  return formatMechValue(v, kind);
}

export function rangeText(def: Pick<Mechanism, 'kind' | 'travelMin' | 'travelMax'>): string {
  return `行程 ${valueText(def.travelMin, def.kind)} 到 ${valueText(def.travelMax, def.kind)}`;
}

export function scopeLabel(scope: ScopeKey, count: number): string {
  if (scope === 'all') return `整排一起（${count}）`;
  if (scope === 'left') return `左半（${count}）`;
  if (scope === 'right') return `右半（${count}）`;
  return '只有這個';
}

export function togetherHint(count: number): string {
  return count > 1 ? `${count} 個一起動，每個加一樣的量（各自不會超出行程）。` : '';
}

export function hoistHint(linked: boolean): string {
  return linked
    ? '左右一起動。取消「左右一起」可以單獨調一邊，讓它斜一點。'
    : '現在一邊一邊調；要一起動就按「左右一起」。';
}

/** 一排機關的名字：成員名字共同的開頭（去掉後面的編號）；看不出來 → 「一排機關」 */
export function groupLabel(names: string[]): string {
  if (names.length === 0) return '一排機關';
  let p = names[0];
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < p.length && i < n.length && p[i] === n[i]) i++;
    p = p.slice(0, i);
  }
  p = p.replace(/[\s\d\-_#（(]+$/u, '').trim();
  return p.length > 0 ? p : '一排機關';
}

export function barText(count: number): string {
  return `改了 ${count} 個機關`;
}

export const ADJUST_TEXT = {
  listTitle: '機關',
  listHint: '在 3D 畫面點有機關的物件，或從這裡選。點組名全選整組，方塊是一片一片，Ctrl 加減選、Shift 連選。橘色的是這個 cue 改過的。按住 Alt 拖把手不對齊，拖空白處轉視角。改的東西要按「存回 Cue 庫」或「另存成新 cue」才會存。',
  noMechanisms: '這個專案還沒有設定機關（PM 在編輯頁的「機關」設定）。',
  backToList: '清單',
  revert: '回到存的值',
  discardAll: '放棄全部',
  unsavedHint: '改的東西要按 3D 畫面下面的「存回 Cue 庫」或「另存成新 cue」才會存；沒存就換 cue、切回審片會放棄。',
  selectedHint: '拖 3D 上的綠色把手，或用下面的按鈕、滑桿調整。按住 Alt 不對齊。',
  moreTools: '更多選取方式',
  confirmTitle: '放棄這些改動？',
  confirmMessage: (n: number) => `調整舞台改了 ${n} 個機關，還沒存。`,
  confirmLabel: '放棄',
  unload: '調整舞台改的東西還沒存，離開就沒了',
  dropped: (n: number) => `換到別的 cue 了，剛剛調整舞台改的 ${n} 個機關已經放棄`,
  playHint: '180° 和 −180° 看起來一樣，差在轉的方向，按上面的「播轉場」看。',
  // 轉的方向
  spinTitle: '轉向',
  angleTitle: '角度',
  positionTitle: '位置',
  spinCw: '順轉',
  spinCcw: '逆轉',
  spinHint: '從上面看。最後停的角度不變，只改從上一個位置怎麼轉過去，不用自己打負數。',
  // 一排旋轉機關：左右對稱
  mirrorTitle: '左右對稱',
  mirrorSide: (side: '左' | '右', dir: 'cw' | 'ccw') => `${side} ${dir === 'cw' ? '順轉' : '逆轉'}`,
  mirrorLeftCw: '左半順時針、右半逆時針',
  mirrorLeftCcw: '左半逆時針、右半順時針',
  mirrorHint: '一次設好整排：每片停的角度不變，只改轉的方向。左右是觀眾看過去的左右，正中間那片不動。',
  mirrorNoSplit: '這一排現在分不出左右（要先在 3D 畫面裡看到它們）。',
  mirrorOn: (n: number) => `這一排共 ${n} 片`,
  // 多選
  quickPick: '快速選',
  pickAll: '全選',
  pickRotary: '全部旋轉',
  pickArc: '全部橫移',
  pickGroup: (title: string, n: number) => `全選「${title}」（${n}）`,
  rowTogether: (title: string) => `整排一起：把手和滑桿一次動「${title}」整排`,
  pickLinear: '全部直線',
  multiTitle: (n: number) => `選了 ${n} 個機關`,
  multiSub: '下面的設定會一次套用到全部，整批算一次復原。Ctrl 加減選、Shift 連選。',
  multiClear: '取消選取',
  multiPosition: '位置',
  multiPositionTip: '拉滑桿或打數字，全部變成同一個值，各自夾在自己的行程內。一次拖曳算一步復原。',
  multiAngleTip: '拉滑桿或打數字，全部變成同一個角度，各自夾在自己的行程內。一次拖曳算一步復原。',
  multiDifferent: '各不相同',
  multiUnitsDiffer: '單位不同',
  multiMixed: '選到的機關單位不同（有的用度、有的用公尺），所以不能一起設位置。只選同一種就可以。',
  multiRange: '各自夾在自己的行程內。',
  multiSpinApplied: (applied: number, same: number, skipped: number) =>
    `${applied > 0 ? `改了 ${applied} 個` : '沒有要改的'}${same > 0 ? `，${same} 個本來就是這個方向` : ''}${skipped > 0 ? `；略過 ${skipped} 個（橫移或升降沒有方向，或沒有轉動、行程不夠）` : ''}。`,
  multiSpinNone: '選到的機關裡沒有能轉方向的（只有垂直轉軸的旋轉機關有順時針 / 逆時針）。',
  multiSpinCount: (n: number, total: number) => (n < total ? `只套用到能轉方向的 ${n} 個，其餘 ${total - n} 個略過。` : `${n} 個旋轉機關。`),
  multiTimingDiff: '各自不同',
  seqTitle: '依序開始',
  seqInterval: '每個間隔',
  seqByPicked: '照選的順序',
  seqByLeft: '由左到右',
  seqApply: '依序開始',
  seqHint: '第 1 個馬上動，之後每個晚一個間隔才開始動；走幾秒不變。',
  seqDone: (n: number) => `已把 ${n} 個機關排成依序開始。`,
  seqBad: '請打 0 到 3600 之間的秒數。',
  // 復原
  undo: '復原',
  noUndo: '沒有可以復原的步驟',
  undoTitle: '復原上一步（整排對稱、一次拖曳都算一步）',
  // 每片的動作時間
  timingTitle: '動作時間',
  timingDelay: '幾秒後動',
  timingDuration: '走幾秒',
  timingBlank: '跟著 cue',
  timingHint: (sec: number) => `空白 = 跟著這個 cue 的轉場（${Math.round(sec * 100) / 100} 秒）；轉場一開始就算起。`,
  timingBad: '請打 0 到 3600 之間的秒數，或留空。',
  timingTogether: (n: number) => `${n} 個一起改成一樣的時間。`,
  linked: '左右一起',
  modeReview: '審片',
  modeAdjust: '調整舞台',
  modeHint: '調整舞台：點 3D 物件、拖把手調機關，調好可以存回 Cue 庫',
} as const;
